// Reward Service — Firestore reads and writes for coins, EXP, and levels.
// Handles both student users (users collection) and child profiles (profiles collection).
// Also maintains a dedicated userRewards collection for the full reward state.

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  increment,
  serverTimestamp,
  arrayUnion,
  runTransaction
} from 'firebase/firestore'
import { db } from '@/firebase/config'
import { getMissionProgress } from '@/services/gameMissionProgress'
import { getChildMissionProgress } from '@/services/childMissionProgress'
import {
  COINS_PER_UNITY_GAME,
  COINS_PER_MISSION_COMPLETE,
  MAX_COINS_PER_GAME,
  MIN_COINS_FOR_ATTEMPT,
  EXP_PER_UNITY_GAME,
  EXP_PER_MISSION_COMPLETE,
  GAME_TYPES
} from '@/constants/rewardConfig'
import {
  calculateMinigameCoins,
  calculateMinigameExp,
  toScorePercent,
  getLevelFromExp
} from '@/utils/rewardUtils'

// ─── Internal helpers ────────────────────────────────────────────────────────

// Build the userRewards document ID.
// Students: their Firebase UID.
// Children: parentUid_child_childId  (parent's UID is the Firebase auth UID).
function getRewardDocId(userId, childId = null) {
  if (childId) return `${userId}_child_${childId}`
  return userId
}

// Default empty reward document shape.
function defaultRewardDoc(userId, childId = null) {
  return {
    userId,
    childId: childId || null,
    totalCoins: 0,
    totalExp: 0,
    currentLevel: 1,
    levelLabel: 'Rookie',
    updatedAt: serverTimestamp()
  }
}

// Fetch existing userRewards document, or return defaults if it doesn't exist yet.
async function fetchRewardDoc(docId, userId, childId = null) {
  const ref = doc(db, 'userRewards', docId)
  const snap = await getDoc(ref)
  if (snap.exists()) return { ref, data: snap.data() }
  return { ref, data: defaultRewardDoc(userId, childId) }
}

function normalizeMissionId(missionId) {
  if (missionId === undefined || missionId === null || missionId === '') return null
  return String(missionId)
}

function missionCompletionBonusAlreadyIssued(existing, missionId) {
  const mid = normalizeMissionId(missionId)
  if (!mid) return false
  const ids = existing?.missionCompletionBonusMissionIds
  if (Array.isArray(ids) && ids.includes(mid)) return true
  const history = existing?.history
  if (!Array.isArray(history)) return false
  return history.some(
    (e) =>
      e &&
      e.source === 'mission_complete' &&
      normalizeMissionId(e.missionId) === mid
  )
}

async function shouldSkipMinigameRewardsForMissionReplay({
  userId,
  childId,
  missionId
}) {
  const mid = normalizeMissionId(missionId)
  if (!mid) return false
  try {
    if (childId) {
      const data = await getChildMissionProgress(missionId, childId)
      return data?.isComplete === true
    }
    const data = await getMissionProgress(missionId)
    return data?.isComplete === true
  } catch (err) {
    console.warn('rewardService: mission progress check failed', err)
    return false
  }
}

async function syncTotalsToUserOrChildProfile({
  userId,
  childId,
  coinsToAdd,
  expToAdd,
  levelAfter,
  levelLabel
}) {
  if (!childId) {
    try {
      const userRef = doc(db, 'users', userId)
      await updateDoc(userRef, {
        totalCoins: increment(coinsToAdd),
        totalExp: increment(expToAdd),
        currentLevel: levelAfter,
        levelLabel,
        updatedAt: serverTimestamp()
      })
    } catch (err) {
      console.warn('rewardService: could not sync totals to users doc', err)
    }
    return
  }
  try {
    const parentRef = doc(db, 'profiles', userId)
    const parentSnap = await getDoc(parentRef)
    if (parentSnap.exists()) {
      const parentData = parentSnap.data()
      const children = Array.isArray(parentData.children)
        ? parentData.children
        : Object.values(parentData.children || {})

      const updatedChildren = children.map((child) => {
        if (child.childId === childId) {
          return {
            ...child,
            totalCoins: (child.totalCoins || 0) + coinsToAdd,
            totalExp: (child.totalExp || 0) + expToAdd,
            currentLevel: levelAfter,
            levelLabel
          }
        }
        return child
      })

      await updateDoc(parentRef, { children: updatedChildren })
    }
  } catch (err) {
    console.warn('rewardService: could not sync totals to profiles doc', err)
  }
}

// Write the updated totals back to userRewards, users (or profiles for children).
async function persistRewards({ docId, ref, coinsToAdd, expToAdd, userId, childId, source, gameId, missionId, scorePercent }) {
  // 1. Read current totals (use increment atomically so concurrent writes are safe)
  const snap = await getDoc(ref)
  const existing = snap.exists() ? snap.data() : defaultRewardDoc(userId, childId)

  const newTotalCoins = (existing.totalCoins || 0) + coinsToAdd
  const oldTotalExp = existing.totalExp || 0
  const newTotalExp = oldTotalExp + expToAdd

  const levelBeforeObj = getLevelFromExp(oldTotalExp)
  const levelObj = getLevelFromExp(newTotalExp)
  const levelAfter = levelObj.level
  const levelBefore = levelBeforeObj.level
  const didLevelUp = levelAfter > levelBefore

  const historyEntry = {
    source,
    gameId: gameId || null,
    missionId: missionId || null,
    coinsEarned: coinsToAdd,
    expEarned: expToAdd,
    scorePercent: scorePercent !== undefined ? scorePercent : null,
    awardedAt: new Date().toISOString()
  }

  // 2. Write to userRewards collection
  if (snap.exists()) {
    await updateDoc(ref, {
      totalCoins: increment(coinsToAdd),
      totalExp: increment(expToAdd),
      currentLevel: levelAfter,
      levelLabel: levelObj.label,
      history: arrayUnion(historyEntry),
      updatedAt: serverTimestamp()
    })
  } else {
    await setDoc(ref, {
      ...defaultRewardDoc(userId, childId),
      totalCoins: coinsToAdd,
      totalExp: expToAdd,
      currentLevel: levelAfter,
      levelLabel: levelObj.label,
      history: [historyEntry],
      updatedAt: serverTimestamp()
    })
  }

  await syncTotalsToUserOrChildProfile({
    userId,
    childId,
    coinsToAdd,
    expToAdd,
    levelAfter,
    levelLabel: levelObj.label
  })

  return { newTotalCoins, newTotalExp, levelBefore, levelAfter, levelLabel: levelObj.label, didLevelUp }
}

// ─── Public API ───────────────────────────────────────────────────────────────

// Fetch current reward totals for a student or child.
// Returns { totalCoins, totalExp, currentLevel, levelLabel } or defaults if not found.
export async function getUserRewards(userId, childId = null) {
  try {
    const docId = getRewardDocId(userId, childId)
    const ref = doc(db, 'userRewards', docId)
    const snap = await getDoc(ref)
    if (snap.exists()) return snap.data()
    return defaultRewardDoc(userId, childId)
  } catch (err) {
    console.error('rewardService.getUserRewards error:', err)
    return defaultRewardDoc(userId, childId)
  }
}

/**
 * Backfill userRewards/{studentUid} from users/{studentUid} when missions
 * synced totals to the profile doc but no reward wallet exists yet.
 * Leaderboard reads users; DigiShop reads userRewards — this keeps them aligned.
 */
export async function ensureStudentRewardDocFromUserProfile(userId) {
  if (!userId) return false

  try {
    const rewardRef = doc(db, 'userRewards', userId)
    const rewardSnap = await getDoc(rewardRef)
    if (rewardSnap.exists()) return false

    const userSnap = await getDoc(doc(db, 'users', userId))
    if (!userSnap.exists()) return false

    const userData = userSnap.data()
    const totalCoins = Number(userData.totalCoins) || 0
    const totalExp = Number(userData.totalExp) || 0

    if (totalCoins === 0 && totalExp === 0) return false

    const levelObj = getLevelFromExp(totalExp)

    await setDoc(rewardRef, {
      ...defaultRewardDoc(userId, null),
      totalCoins,
      totalExp,
      currentLevel: userData.currentLevel ?? levelObj.level,
      levelLabel: userData.levelLabel ?? levelObj.label
    })

    return true
  } catch (err) {
    console.warn('rewardService.ensureStudentRewardDocFromUserProfile failed', err)
    return false
  }
}

// Award rewards after a minigame (both scored and Unity/completion types).
//
// Params:
//   userId     — Firebase auth UID (always the logged-in user)
//   childId    — child's UUID if playing as a child, null for students
//   gameType   — GAME_TYPES.UNITY | GAME_TYPES.SCORED | GAME_TYPES.COMPLETION
//   gameId     — string identifier e.g. 'animalZoo', 'chatModerator'
//   score      — actual score (only used for SCORED type)
//   maxScore   — max possible score (only used for SCORED type)
//   missionId  — optional, for history tracking
//
// Returns { coinsAwarded, expAwarded, newTotalCoins, newTotalExp, levelBefore, levelAfter, levelLabel, didLevelUp }
export async function awardMinigameRewards({ userId, childId = null, gameType, gameId, score, maxScore, missionId }) {
  if (!userId) return null

  if (
    missionId !== undefined &&
    missionId !== null &&
    missionId !== '' &&
    (await shouldSkipMinigameRewardsForMissionReplay({ userId, childId, missionId }))
  ) {
    return null
  }

  try {
    let coinsToAdd, expToAdd, scorePercent

    if (gameType === GAME_TYPES.SCORED && score !== undefined && maxScore) {
      scorePercent = toScorePercent(score, maxScore)
      coinsToAdd = calculateMinigameCoins(scorePercent)
      expToAdd = calculateMinigameExp(scorePercent)
    } else {
      // Unity or completion type — flat rewards
      coinsToAdd = COINS_PER_UNITY_GAME
      expToAdd = EXP_PER_UNITY_GAME
      scorePercent = null
    }

    const docId = getRewardDocId(userId, childId)
    const { ref } = await fetchRewardDoc(docId, userId, childId)

    const result = await persistRewards({
      docId,
      ref,
      coinsToAdd,
      expToAdd,
      userId,
      childId,
      source: gameType === GAME_TYPES.SCORED ? 'minigame_scored' : 'minigame_unity',
      gameId,
      missionId,
      scorePercent
    })

    return { coinsAwarded: coinsToAdd, expAwarded: expToAdd, ...result }
  } catch (err) {
    console.error('rewardService.awardMinigameRewards error:', err)
    return null
  }
}

// Award the one-time mission completion bonus (coins + EXP) per mission.
// Idempotent: uses a Firestore transaction + missionCompletionBonusMissionIds / history
// so replays, double video end, or badge edge cases cannot double-pay.
//
// Params:
//   isFirstCompletion — if false, skips immediately (caller hint; server still enforces).
//
// Returns same shape as awardMinigameRewards, or null if skipped / already issued.
export async function awardMissionCompleteRewards({ userId, childId = null, missionId, isFirstCompletion }) {
  if (!userId || isFirstCompletion === false) return null
  if (missionId === undefined || missionId === null || missionId === '') return null

  const coinsToAdd = COINS_PER_MISSION_COMPLETE
  const expToAdd = EXP_PER_MISSION_COMPLETE
  const docId = getRewardDocId(userId, childId)
  const ref = doc(db, 'userRewards', docId)
  const mid = normalizeMissionId(missionId)

  try {
    const txResult = await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(ref)
      const existing = snap.exists() ? snap.data() : null

      if (missionCompletionBonusAlreadyIssued(existing || {}, missionId)) {
        return { kind: 'skip' }
      }

      const base = existing || {}
      const oldTotalExp = base.totalExp || 0
      const oldTotalCoins = base.totalCoins || 0
      const newTotalExp = oldTotalExp + expToAdd
      const newTotalCoins = oldTotalCoins + coinsToAdd

      const levelBeforeObj = getLevelFromExp(oldTotalExp)
      const levelObj = getLevelFromExp(newTotalExp)
      const levelAfter = levelObj.level
      const levelBefore = levelBeforeObj.level
      const didLevelUp = levelAfter > levelBefore

      const historyEntry = {
        source: 'mission_complete',
        gameId: null,
        missionId: missionId ?? null,
        coinsEarned: coinsToAdd,
        expEarned: expToAdd,
        scorePercent: null,
        awardedAt: new Date().toISOString()
      }

      if (snap.exists()) {
        transaction.update(ref, {
          totalCoins: increment(coinsToAdd),
          totalExp: increment(expToAdd),
          currentLevel: levelAfter,
          levelLabel: levelObj.label,
          history: arrayUnion(historyEntry),
          missionCompletionBonusMissionIds: arrayUnion(mid),
          updatedAt: serverTimestamp()
        })
      } else {
        transaction.set(ref, {
          ...defaultRewardDoc(userId, childId),
          totalCoins: coinsToAdd,
          totalExp: expToAdd,
          currentLevel: levelAfter,
          levelLabel: levelObj.label,
          history: [historyEntry],
          missionCompletionBonusMissionIds: [mid],
          updatedAt: serverTimestamp()
        })
      }

      return {
        kind: 'ok',
        coinsAwarded: coinsToAdd,
        expAwarded: expToAdd,
        newTotalCoins,
        newTotalExp,
        levelBefore,
        levelAfter,
        levelLabel: levelObj.label,
        didLevelUp
      }
    })

    if (!txResult || txResult.kind === 'skip') return null

    await syncTotalsToUserOrChildProfile({
      userId,
      childId,
      coinsToAdd: txResult.coinsAwarded,
      expToAdd: txResult.expAwarded,
      levelAfter: txResult.levelAfter,
      levelLabel: txResult.levelLabel
    })

    return {
      coinsAwarded: txResult.coinsAwarded,
      expAwarded: txResult.expAwarded,
      newTotalCoins: txResult.newTotalCoins,
      newTotalExp: txResult.newTotalExp,
      levelBefore: txResult.levelBefore,
      levelAfter: txResult.levelAfter,
      levelLabel: txResult.levelLabel,
      didLevelUp: txResult.didLevelUp
    }
  } catch (err) {
    console.error('rewardService.awardMissionCompleteRewards error:', err)
    return null
  }
}
